import { destinationShape } from './track-login-destination';

/**
 * Who sends a signed-in worker to `/login` in the middle of the interview.
 *
 * Observation only: nothing here changes where anybody goes.
 *
 * Why it exists. Since the session re-check in `AuthGuard` (30/09/2026), PostHog still shows
 * interview sessions that hop interview -> `/login?redirect_url=<interview>` -> interview 0.3-2 s
 * after a message is sent: 2 of 53 sending sessions on the first day. The session is valid, no
 * `/accounts/me` is called, and `AuthGuard` logs no `auth_guard_redirect`, so the navigation is
 * not the guard's. A replay rules out the phone's Back button (the keyboard is still open when
 * the hop starts). What is left is a client navigation whose caller nobody has seen. This records
 * it: how it was started (`push`, `replace` or `popstate`), from where, at which history position,
 * and the top of the caller's stack.
 *
 * Nothing personal is logged: paths go through `destinationShape` (ids become `:id`), the query
 * is reduced to whether a destination was carried, and the stack keeps only script file names and
 * positions.
 *
 *   SELECT kind, from_path, count(*) FROM "_rumlog" WHERE message = 'login_navigation' GROUP BY kind, from_path
 */
export type LoginNavigationKind =
  | 'push'
  | 'replace'
  | 'popstate'
  /** Started neither by `push`/`replace` nor by a history move: a navigation Next makes on its own. */
  | 'unknown';

export interface LoginNavigationEvent {
  kind: LoginNavigationKind;
  fromPath: string;
  toPath: string;
  hasRedirectUrl: boolean;
  historyIndex: number | null;
  stack: string;
}

/** The part of the Next router this needs, so a test can hand in a fake. */
export interface TraceableRouter {
  asPath: string;
  push: (...args: any[]) => Promise<boolean>;
  replace: (...args: any[]) => Promise<boolean>;
  events: {
    on: (event: 'routeChangeStart', handler: (url: string) => void) => void;
    off: (event: 'routeChangeStart', handler: (url: string) => void) => void;
  };
}

const LOGIN_PATH = /^(?:\/[a-z]{2})?\/login\/?$/;

const urlOf = (target: unknown): string => {
  if (typeof target === 'string') return target;
  if (target && typeof target === 'object') return (target as { pathname?: string }).pathname ?? '';
  return '';
};

const pathOf = (url: string) => url.split(/[?#]/, 1)[0];
const isLoginUrl = (url: string) => LOGIN_PATH.test(pathOf(url));
const carriesRedirect = (url: string) => /[?&]redirect_url=/.test(url) || /[?&]redirectTo=/.test(url);

/**
 * The top of the caller's stack, script names and positions only: `chunk.js:1:2345`. Minified, but
 * it maps back through the source maps of the build, and it carries no page data.
 */
export const callerStack = (stack: string | undefined, frames = 8): string =>
  (stack ?? '')
    .split('\n')
    .slice(1)
    .map((line) => {
      const match = line.match(/([^/\s()]+\.js(?:\?[^:\s)]*)?):(\d+):(\d+)/);
      return match ? `${match[1].split('?')[0]}:${match[2]}:${match[3]}` : '';
    })
    .filter(Boolean)
    .slice(0, frames)
    .join(' < ');

const historyIndex = (): number | null => {
  try {
    const idx = (window.history.state as { idx?: unknown } | null)?.idx;
    return typeof idx === 'number' ? idx : null;
  } catch {
    return null;
  }
};

/**
 * Wraps `push` and `replace` on the router instance and listens to `popstate`, then reports every
 * route change whose destination is `/login`. Returns the function that undoes all of it.
 *
 * The method wrappers mark `push` and `replace`. A history move is recognised by a `popstate` seen
 * in the two seconds before the route change (listened to in the capture phase, so it runs before
 * Next's own handler). Anything else is `unknown`, never guessed as `push`.
 */
export function installLoginNavigationTrace(
  router: TraceableRouter,
  report: (event: LoginNavigationEvent) => void
): () => void {
  let pending: { kind: LoginNavigationKind; stack: string; fromPath: string; idx: number | null } | null = null;

  const originalPush = router.push;
  const originalReplace = router.replace;

  const wrap =
    (kind: 'push' | 'replace', original: TraceableRouter['push']) =>
    (...args: any[]) => {
      // `as` is what the address bar shows; fall back to `url`.
      const target = urlOf(args[1] ?? args[0]);
      if (isLoginUrl(target)) {
        pending = { kind, stack: callerStack(new Error().stack), fromPath: router.asPath, idx: historyIndex() };
      }
      return original.apply(router, args);
    };

  router.push = wrap('push', originalPush);
  router.replace = wrap('replace', originalReplace);

  let lastPopState: { at: number; fromPath: string; idx: number | null } | null = null;
  const onPopState = () => {
    lastPopState = { at: Date.now(), fromPath: router.asPath, idx: historyIndex() };
  };

  const onRouteChangeStart = (url: string) => {
    if (!isLoginUrl(url)) {
      pending = null;
      lastPopState = null;
      return;
    }
    // A push/replace wrapper sets `pending` synchronously before Next starts the change. Otherwise
    // a history move in the last two seconds is the origin: Next's own popstate handling awaits
    // before it emits `routeChangeStart`, so the timestamp is read well after it was written.
    const fromHistory =
      !pending && lastPopState && Date.now() - lastPopState.at < 2000
        ? { kind: 'popstate' as const, stack: '', fromPath: lastPopState.fromPath, idx: lastPopState.idx }
        : null;
    const origin = pending ?? fromHistory;
    pending = null;
    lastPopState = null;
    try {
      report({
        kind: origin?.kind ?? 'unknown',
        fromPath: destinationShape(origin?.fromPath ?? router.asPath),
        toPath: destinationShape(url),
        hasRedirectUrl: carriesRedirect(url),
        historyIndex: origin?.idx ?? historyIndex(),
        stack: origin?.stack ?? '',
      });
    } catch {
      // Observation must never break a navigation.
    }
  };

  // Capture phase: at the target, capture listeners run before Next's own (registered earlier, bubble).
  window.addEventListener('popstate', onPopState, { capture: true });
  router.events.on('routeChangeStart', onRouteChangeStart);

  return () => {
    router.push = originalPush;
    router.replace = originalReplace;
    window.removeEventListener('popstate', onPopState, { capture: true });
    router.events.off('routeChangeStart', onRouteChangeStart);
  };
}

/** Fire-and-forget on the O2 logs stream, like `login_destination`. Never throws. */
export function trackLoginNavigation(event: LoginNavigationEvent): void {
  if (typeof window === 'undefined') return;
  import('@openobserve/browser-logs')
    .then(({ openobserveLogs }) => {
      openobserveLogs.logger.info('login_navigation', {
        event: 'login_navigation',
        kind: event.kind,
        from_path: event.fromPath,
        to_path: event.toPath,
        has_redirect_url: event.hasRedirectUrl,
        history_index: event.historyIndex,
        stack: event.stack,
      });
    })
    .catch(() => {
      // Swallow: an uninitialised SDK must never surface to the user.
    });
}
